<template>
  <div class="w-full h-full flex flex-col px-4 sm:px-6 lg:px-8 pt-4 md:pt-6 pb-4 md:pb-8 overflow-y-auto overflow-x-hidden">
    <!-- Dual Tab Switcher & Desktop CTA Row (AC1) -->
    <div class="flex items-center justify-between gap-[24px] mb-[16px] md:mb-[20px] shrink-0 border-b border-gray-200">
      <div class="flex items-center gap-[24px] overflow-x-auto hide-scrollbar">
        <button
          type="button"
          class="pb-[10px] text-[18px] font-bold font-['Raleway',sans-serif] transition-colors border-b-2 cursor-pointer bg-transparent border-t-0 border-x-0 outline-none whitespace-nowrap shrink-0"
          :class="activeTabName === 'cohorts' ? 'text-black border-[#01d680]' : 'text-[#9b9b9b] border-transparent hover:text-black'"
          @click="switchTab('cohorts')"
        >
          Cohorts
        </button>
        <button
          type="button"
          class="pb-[10px] text-[18px] font-bold font-['Raleway',sans-serif] transition-colors border-b-2 cursor-pointer bg-transparent border-t-0 border-x-0 outline-none whitespace-nowrap shrink-0"
          :class="activeTabName === 'templates' ? 'text-black border-[#01d680]' : 'text-[#9b9b9b] border-transparent hover:text-black'"
          @click="switchTab('templates')"
        >
          Cohort Templates
        </button>
      </div>

      <!-- Desktop Create CTA (docked right of tab switcher row on desktop viewports) -->
      <div
        v-if="activeTabName === 'cohorts' && hasCohorts"
        class="hidden md:block pb-[8px] -mt-1.5 shrink-0"
      >
        <mount-on-click
          v-if="canCreate"
          data-template-id="add-cohort-wizard-template"
        >
          <button
            type="button"
            class="flex items-center justify-center px-5 py-2.5 rounded-full bg-[#01d680] text-[#043f42] font-['Raleway',sans-serif] font-bold text-sm hover:bg-[#00c270] transition-colors cursor-pointer shadow-none"
          >
            Create Cohort
          </button>
        </mount-on-click>
        <subscription-gate v-else>
          <button
            type="button"
            class="flex items-center justify-center px-5 py-2.5 rounded-full bg-[#01d680] text-[#043f42] font-['Raleway',sans-serif] font-bold text-sm hover:bg-[#00c270] transition-colors cursor-pointer shadow-none"
          >
            Create Cohort
          </button>
        </subscription-gate>
      </div>
    </div>

    <!-- Desktop Controls Row (>= 1024px) -->
    <div class="hidden lg:flex items-center justify-between w-full mb-[20px] shrink-0 gap-3">
      <!-- Left side: Filter Pills in horizontal stream -->
      <div class="flex items-center gap-2 overflow-x-auto hide-scrollbar py-0.5 flex-1 min-w-0">
        <a
          v-for="pill in statusPills"
          :key="pill.key"
          :href="filterUrl(pill.key)"
          :class="pillClass(pill.key)"
          data-prefetch="false"
          @click.prevent="setFilter(pill.key)"
        >
          {{ pill.label }}
        </a>

        <!-- Visible Select all on page button (Desktop) -->
        <button
          v-if="activeTabName === 'cohorts' && paginatedCohorts.length > 0"
          type="button"
          class="flex items-center gap-1.5 px-[14px] py-[8px] rounded-[38px] border border-gray-300 bg-white hover:bg-gray-50 text-[#043f42] text-[13px] font-['Raleway',sans-serif] font-semibold transition-colors cursor-pointer shrink-0 shadow-none ml-2"
          title="Select all cohorts on this page"
          @click="selectAllVisible"
        >
          <svg
            class="w-4 h-4 text-[#01d680]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
            />
            <polyline points="9 11 12 14 22 4" />
          </svg>
          <span>Select all</span>
        </button>

        <button
          v-if="activeTabName === 'templates' && filteredTemplates.length > 0"
          type="button"
          class="flex items-center gap-1.5 px-[14px] py-[8px] rounded-[38px] border border-gray-300 bg-white hover:bg-gray-50 text-[#043f42] text-[13px] font-['Raleway',sans-serif] font-semibold transition-colors cursor-pointer shrink-0 shadow-none ml-2"
          title="Select all templates on this page"
          @click="selectAllVisibleTemplates"
        >
          <svg
            class="w-4 h-4 text-[#01d680]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
            />
            <polyline points="9 11 12 14 22 4" />
          </svg>
          <span>Select all</span>
        </button>
      </div>

      <!-- Right side: Search + Sort -->
      <div class="flex items-center gap-[8px] shrink-0">
        <!-- Search -->
        <div
          class="h-[43px] bg-white rounded-full px-4 flex items-center gap-[9px] shadow-none border border-transparent focus-within:border-[#01d680] transition-all"
          :class="activeTabName === 'templates' ? 'w-[250px]' : 'w-[216px]'"
        >
          <button
            type="button"
            class="search-icon-btn p-0 m-0 bg-transparent border-none outline-none flex items-center justify-center shrink-0 cursor-pointer"
            tabindex="-1"
            @click="searchQuery ? clearSearch() : submitSearch()"
          >
            <svg
              v-if="!searchQuery"
              width="14"
              height="14"
              viewBox="0 0 18 18"
              fill="none"
              class="text-[#5b5c60] shrink-0"
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <line
                x1="13"
                y1="13"
                x2="17"
                y2="17"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
            <svg
              v-else
              class="w-[13px] h-[13px] text-[#5b5c60] hover:text-[#043f42] transition-colors cursor-pointer"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="activeTabName === 'templates' ? 'Search cohort templates' : 'Search cohorts'"
            class="bg-transparent border-none outline-none text-[#000000] text-[13px] font-['Raleway',sans-serif] font-normal w-full placeholder:text-[#5b5c60] m-0 p-0 shadow-none focus:ring-0 whitespace-nowrap"
            autocomplete="off"
            @input="onSearchInput"
            @keydown.enter="submitSearch"
          >
        </div>

        <!-- Sort Dropdown -->
        <div class="relative">
          <button
            id="cohorts-filter-sort-btn"
            type="button"
            aria-label="Filter and sort cohorts"
            :aria-expanded="isFilterSortOpen"
            class="w-[43px] h-[43px] bg-white rounded-full flex items-center justify-center shadow-none cursor-pointer shrink-0 hover:bg-gray-50 border border-transparent focus-visible:ring-2 focus-visible:ring-[#01d680] transition-colors"
            :class="{ 'border-[#01d680] text-[#01d680]': isFilterSortOpen || currentSort !== 'date-desc' }"
            @click.stop="toggleFilterSort"
          >
            <svg
              viewBox="0 0 18.5 17.5"
              fill="none"
              class="w-[19px] h-[18px]"
            >
              <path
                d="M18.5 8.787l-12.355 0m-4.361 0l-1.784 0m18.5 6.607l-5.748 0m-4.361 0l-8.391 0m18.5-13.214l-3.105 0m-4.361 0l-11.034 0m13.214 2.18c0.28628 0 0.56976-0.05639 0.83425-0.16594 0.26449-0.10956 0.50481-0.27013 0.70724-0.47257 0.20243-0.20243 0.36301-0.44275 0.47257-0.70724 0.10955-0.26449 0.16594-0.54797 0.16594-0.83425 0-0.28628-0.05639-0.56976-0.16594-0.83425-0.10956-0.26449-0.27013-0.50481-0.47257-0.70724-0.20243-0.20243-0.44275-0.36301-0.70724-0.47257-0.26449-0.10956-0.54797-0.16594-0.83425-0.16594-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m-9.25 6.607c0.57817 0 1.13266-0.22968 1.54149-0.63851 0.40883-0.40883 0.63851-0.96332 0.63851-1.54149 0-0.57817-0.22968-1.13266-0.63851-1.54149-0.40883-0.40883-0.96332-0.63851-1.54149-0.63851-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m6.607 6.608c0.5783 0 1.13292-0.22973 1.54185-0.63865 0.40892-0.40892 0.63915-0.96404 0.63915-1.54235 0-0.5783-0.23023-1.13242-0.63915-1.54135-0.40892-0.40892-0.96354-0.63865-1.54185-0.63865-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13267 0.63851 1.54149 0.40883 0.40883 0.96332 0.63951 1.54149 0.63951z"
                stroke="currentColor"
                stroke-width="1.564"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div
            v-if="isFilterSortOpen"
            class="absolute top-[48px] right-0 w-[220px] bg-white shadow-2xl rounded-[14px] z-[100] flex flex-col py-2 border border-black/10 font-['Raleway',sans-serif]"
            @click.stop
          >
            <div class="px-4 py-1.5 text-[11px] text-[#5b5c60] uppercase tracking-wider font-bold">
              Sort by
            </div>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-desc')"
            >
              <span>Newest First</span>
              <span v-if="currentSort === 'date-desc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-asc')"
            >
              <span>Oldest First</span>
              <span v-if="currentSort === 'date-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-asc')"
            >
              <span>Name (A – Z)</span>
              <span v-if="currentSort === 'name-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-desc')"
            >
              <span>Name (Z – A)</span>
              <span v-if="currentSort === 'name-desc'">✓</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile & Tablet Controls (2-Tier Architecture, < 1024px) -->
    <div class="flex lg:hidden flex-col gap-2.5 mb-[16px] w-full shrink-0">
      <!-- Tier 1: Search (flex-1) + Sort Button (40px) + Mobile CTA -->
      <div class="flex items-center gap-2 w-full">
        <div class="h-[40px] bg-white rounded-full px-3.5 flex items-center gap-2 shadow-none border border-transparent focus-within:border-[#01d680] flex-1 min-w-0 transition-all">
          <button
            type="button"
            class="p-0 m-0 bg-transparent border-none outline-none flex items-center justify-center shrink-0 cursor-pointer"
            tabindex="-1"
            @click="searchQuery ? clearSearch() : submitSearch()"
          >
            <svg
              v-if="!searchQuery"
              width="14"
              height="14"
              viewBox="0 0 18 18"
              fill="none"
              class="text-[#5b5c60] shrink-0"
            >
              <circle
                cx="8"
                cy="8"
                r="6"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <line
                x1="13"
                y1="13"
                x2="17"
                y2="17"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
            <svg
              v-else
              class="w-[13px] h-[13px] text-[#5b5c60] hover:text-[#043f42] transition-colors cursor-pointer"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="activeTabName === 'templates' ? 'Search templates' : 'Search cohorts'"
            class="bg-transparent border-none outline-none text-[#000000] text-[13px] font-['Raleway',sans-serif] font-normal w-full placeholder:text-[#5b5c60] m-0 p-0 shadow-none focus:ring-0 whitespace-nowrap"
            autocomplete="off"
            @input="onSearchInput"
            @keydown.enter="submitSearch"
          >
        </div>

        <!-- Mobile Sort Button -->
        <div class="relative shrink-0">
          <button
            type="button"
            aria-label="Filter and sort cohorts"
            :aria-expanded="isFilterSortOpen"
            class="w-[40px] h-[40px] bg-white rounded-full flex items-center justify-center shadow-none cursor-pointer hover:bg-gray-50 border border-transparent focus-visible:ring-2 focus-visible:ring-[#01d680] transition-colors shrink-0"
            :class="{ 'border-[#01d680] text-[#01d680]': isFilterSortOpen || currentSort !== 'date-desc' }"
            @click.stop="toggleFilterSort"
          >
            <svg
              viewBox="0 0 18.5 17.5"
              fill="none"
              class="w-[18px] h-[17px]"
            >
              <path
                d="M18.5 8.787l-12.355 0m-4.361 0l-1.784 0m18.5 6.607l-5.748 0m-4.361 0l-8.391 0m18.5-13.214l-3.105 0m-4.361 0l-11.034 0m13.214 2.18c0.28628 0 0.56976-0.05639 0.83425-0.16594 0.26449-0.10956 0.50481-0.27013 0.70724-0.47257 0.20243-0.20243 0.36301-0.44275 0.47257-0.70724 0.10955-0.26449 0.16594-0.54797 0.16594-0.83425 0-0.28628-0.05639-0.56976-0.16594-0.83425-0.10956-0.26449-0.27013-0.50481-0.47257-0.70724-0.20243-0.20243-0.44275-0.36301-0.70724-0.47257-0.26449-0.10956-0.54797-0.16594-0.83425-0.16594-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m-9.25 6.607c0.57817 0 1.13266-0.22968 1.54149-0.63851 0.40883-0.40883 0.63851-0.96332 0.63851-1.54149 0-0.57817-0.22968-1.13266-0.63851-1.54149-0.40883-0.40883-0.96332-0.63851-1.54149-0.63851-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m6.607 6.608c0.5783 0 1.13292-0.22973 1.54185-0.63865 0.40892-0.40892 0.63915-0.96404 0.63915-1.54235 0-0.5783-0.23023-1.13242-0.63915-1.54135-0.40892-0.40892-0.96354-0.63865-1.54185-0.63865-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13267 0.63851 1.54149 0.40883 0.40883 0.96332 0.63951 1.54149 0.63951z"
                stroke="currentColor"
                stroke-width="1.564"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div
            v-if="isFilterSortOpen"
            class="absolute top-[44px] right-0 w-[210px] bg-white shadow-2xl rounded-[14px] z-[100] flex flex-col py-2 border border-black/10 font-['Raleway',sans-serif]"
            @click.stop
          >
            <div class="px-4 py-1.5 text-[11px] text-[#5b5c60] uppercase tracking-wider font-bold">
              Sort by
            </div>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-desc')"
            >
              <span>Newest First</span>
              <span v-if="currentSort === 'date-desc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-asc')"
            >
              <span>Oldest First</span>
              <span v-if="currentSort === 'date-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-asc')"
            >
              <span>Name (A – Z)</span>
              <span v-if="currentSort === 'name-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-desc')"
            >
              <span>Name (Z – A)</span>
              <span v-if="currentSort === 'name-desc'">✓</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Tier 2: Dedicated horizontally scrollable container for filter pills -->
      <div class="overflow-x-auto hide-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 flex items-center gap-2 py-1 shrink-0">
        <a
          v-for="pill in statusPills"
          :key="pill.key"
          :href="filterUrl(pill.key)"
          :class="pillClass(pill.key)"
          data-prefetch="false"
          @click.prevent="setFilter(pill.key)"
        >
          {{ pill.label }}
        </a>

        <!-- Visible Select all on page button -->
        <button
          v-if="activeTabName === 'cohorts' && paginatedCohorts.length > 0"
          type="button"
          class="flex items-center gap-1.5 px-[14px] py-[8px] rounded-[38px] border border-gray-300 bg-white hover:bg-gray-50 text-[#043f42] text-[13px] font-['Raleway',sans-serif] font-semibold transition-colors cursor-pointer shrink-0 shadow-none ml-2"
          title="Select all cohorts on this page"
          @click="selectAllVisible"
        >
          <svg
            class="w-4 h-4 text-[#01d680]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
            />
            <polyline points="9 11 12 14 22 4" />
          </svg>
          <span>Select all</span>
        </button>

        <button
          v-if="activeTabName === 'templates' && filteredTemplates.length > 0"
          type="button"
          class="flex items-center gap-1.5 px-[14px] py-[8px] rounded-[38px] border border-gray-300 bg-white hover:bg-gray-50 text-[#043f42] text-[13px] font-['Raleway',sans-serif] font-semibold transition-colors cursor-pointer shrink-0 shadow-none ml-2"
          title="Select all templates on this page"
          @click="selectAllVisibleTemplates"
        >
          <svg
            class="w-4 h-4 text-[#01d680]"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
            />
            <polyline points="9 11 12 14 22 4" />
          </svg>
          <span>Select all</span>
        </button>
      </div>
    </div>

    <!-- Bulk Action Bar -->
    <div
      v-if="activeTabName === 'cohorts' && selectedCohortIds.length > 0"
      class="w-full bg-[#f8fff8] border border-[#01d680] rounded-[14px] px-[20px] py-[10px] mb-[16px] flex items-center justify-between shadow-none z-40 transition-all duration-200 shrink-0"
    >
      <div class="flex items-center gap-[16px]">
        <span class="text-[14px] font-bold text-[#043f42] font-['Raleway',sans-serif]">
          {{ selectedCohortIds.length }} selected
        </span>
        <button
          type="button"
          class="text-[13px] text-[#01d680] hover:text-[#00c576] font-semibold underline cursor-pointer bg-transparent border-none p-0 font-['Raleway',sans-serif]"
          @click="selectAllVisible"
        >
          Select all on page ({{ Math.min(10, paginatedCohorts.length) }})
        </button>
        <button
          type="button"
          class="text-[13px] text-[#5b5c60] hover:text-black cursor-pointer bg-transparent border-none p-0 ml-[4px] font-['Raleway',sans-serif]"
          @click="clearSelection"
        >
          Clear
        </button>
      </div>

      <div class="flex items-center gap-[10px]">
        <button
          type="button"
          class="flex items-center gap-[6px] px-[16px] py-[8px] rounded-full bg-[#dc2626] hover:bg-red-700 text-white font-['Raleway',sans-serif] text-[13px] font-bold transition-colors cursor-pointer border-none shadow-none"
          @click="openBulkDeleteModal"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Delete selected ({{ selectedCohortIds.length }})
        </button>
      </div>
    </div>

    <!-- Template Bulk Action Bar -->
    <div
      v-if="activeTabName === 'templates' && selectedTemplateIds.length > 0"
      class="w-full bg-[#f8fff8] border border-[#01d680] rounded-[14px] px-[20px] py-[10px] mb-[16px] flex items-center justify-between shadow-none z-40 transition-all duration-200 shrink-0"
    >
      <div class="flex items-center gap-[16px]">
        <span class="text-[14px] font-bold text-[#043f42] font-['Raleway',sans-serif]">
          {{ selectedTemplateIds.length }} selected
        </span>
        <button
          type="button"
          class="text-[13px] text-[#01d680] hover:text-[#00c576] font-semibold underline cursor-pointer bg-transparent border-none p-0 font-['Raleway',sans-serif]"
          @click="selectAllVisibleTemplates"
        >
          Select all on page ({{ Math.min(10, filteredTemplates.length) }})
        </button>
        <button
          type="button"
          class="text-[13px] text-[#5b5c60] hover:text-black cursor-pointer bg-transparent border-none p-0 ml-[4px] font-['Raleway',sans-serif]"
          @click="clearTemplateSelection"
        >
          Clear
        </button>
      </div>

      <div class="flex items-center gap-[10px]">
        <button
          type="button"
          class="flex items-center gap-[6px] px-[16px] py-[8px] rounded-full bg-[#dc2626] hover:bg-red-700 text-white font-['Raleway',sans-serif] text-[13px] font-bold transition-colors cursor-pointer border-none shadow-none"
          @click="openBulkDeleteTemplateModal"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
          Delete selected ({{ selectedTemplateIds.length }})
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 w-full relative">
      <!-- Templates Tab Content -->
      <div
        v-if="activeTabName === 'templates'"
        class="w-full h-full flex flex-col pt-[16px]"
      >
        <div
          v-if="filteredTemplates.length === 0"
          class="border-2 border-dashed border-[#d1d5db] rounded-[25px] p-[50px] flex flex-col items-center justify-center text-center min-h-[400px] w-full max-w-[800px] mx-auto bg-white/50"
        >
          <div class="w-[64px] h-[64px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center mb-[20px]">
            <svg
              class="w-[28px] h-[28px] text-[#01d680]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
              />
              <line
                x1="9"
                y1="3"
                x2="9"
                y2="21"
              />
            </svg>
          </div>
          <h3 class="text-[#043f42] font-['Raleway',sans-serif] font-bold text-[20px] mb-[10px]">
            No cohort templates found
          </h3>
          <p class="text-[#5b5c60] font-['Raleway',sans-serif] text-[15px] mb-[10px]">
            {{ searchQuery ? 'No templates matched your search query.' : 'You can elevate any configured live cohort into a template from its kebab menu.' }}
          </p>
        </div>

        <div
          v-else
          class="w-full h-full flex flex-col justify-between"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[18px] sm:gap-[20px] w-full items-start">
            <CohortTemplateCard
              v-for="tpl in filteredTemplates"
              :key="tpl.id"
              :template="tpl"
              :is-selected="selectedTemplateIds.includes(tpl.id)"
              @toggle-select="toggleSelectTemplate"
              @instantiate="openInstantiateModal"
              @clone="cloneTemplate"
              @archive="archiveTemplate"
              @delete="deleteTemplate"
            />
          </div>

          <!-- Templates Pagination -->
          <div
            v-if="currentPagy"
            class="flex items-center justify-between mt-[32px] pt-[16px] border-t border-gray-200/60 w-full shrink-0"
          >
            <div class="text-[14px] text-[#5b5c60] font-['Raleway',sans-serif]">
              Showing {{ currentPagy.from }}–{{ currentPagy.to }} of {{ currentPagy.count }} cohort templates
            </div>
            <div class="flex items-center gap-[12px]">
              <a
                v-if="currentPagy.prev"
                :href="pageUrl(currentPagy.prev)"
                data-prefetch="false"
                class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
                @click.prevent="goToPage(currentPagy.prev)"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M13.5 8.5L9.5 12L13.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </a>
              <div
                v-else
                class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M13.5 8.5L9.5 12L13.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </div>
              <span class="text-[14px] font-semibold text-[#000000] font-['Raleway',sans-serif] px-1">
                Page {{ currentPagy.page }} of {{ currentPagy.last }}
              </span>
              <a
                v-if="currentPagy.next"
                :href="pageUrl(currentPagy.next)"
                data-prefetch="false"
                class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
                @click.prevent="goToPage(currentPagy.next)"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M10.5 8.5L14.5 12L10.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </a>
              <div
                v-else
                class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M10.5 8.5L14.5 12L10.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cohorts Tab Content -->
      <div
        v-else
        class="w-full h-full"
      >
        <div
          v-if="filteredCohorts.length === 0"
          class="w-full h-full flex flex-col items-center justify-center pt-[50px]"
        >
          <div
            v-if="totalCount === 0 && !searchQuery"
            class="border-2 border-dashed border-[#d1d5db] rounded-[25px] p-[50px] flex flex-col items-center justify-center text-center min-h-[400px] w-full max-w-[800px] bg-white/50"
          >
            <div class="w-[64px] h-[64px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center mb-[20px]">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="text-[#01d680]"
              ><line
                x1="12"
                y1="5"
                x2="12"
                y2="19"
              /><line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              /></svg>
            </div>
            <h3 class="text-[#043f42] font-['Raleway',sans-serif] font-bold text-[20px] mb-[10px]">
              No cohorts yet
            </h3>
            <p class="text-[#5b5c60] font-['Raleway',sans-serif] text-[15px] mb-[30px]">
              {{ canCreate ? 'Create your first cohort to get started.' : 'No cohorts have been created in this account yet.' }}
            </p>
            <mount-on-click
              v-if="canCreate"
              data-template-id="add-cohort-wizard-template"
            >
              <button
                type="button"
                class="flex items-center justify-center px-5 py-2.5 rounded-full bg-[#01d680] text-[#043f42] font-['Raleway',sans-serif] font-bold text-sm hover:bg-[#00c270] transition-colors cursor-pointer shadow-none"
              >
                Create Cohort
              </button>
            </mount-on-click>
          </div>
          <div
            v-else
            class="border-2 border-dashed border-[#d1d5db] rounded-[25px] p-[50px] flex flex-col items-center justify-center text-center min-h-[400px] w-full max-w-[800px] bg-white/50"
          >
            <div class="w-[64px] h-[64px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center mb-[20px]">
              <svg
                class="w-[28px] h-[28px] text-[#5b5c60]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              ><circle
                cx="11"
                cy="11"
                r="8"
              /><line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
              /></svg>
            </div>
            <h3 class="text-[#043f42] font-['Raleway',sans-serif] font-bold text-[20px] mb-[10px]">
              {{ emptyStateTitle }}
            </h3>
            <p class="text-[#5b5c60] font-['Raleway',sans-serif] text-[15px] mb-[25px]">
              {{ emptyStateSubtitle }}
            </p>
            <button
              v-if="searchQuery"
              type="button"
              class="px-5 py-2.5 rounded-[38px] border border-black text-black font-['Raleway',sans-serif] font-bold text-sm hover:bg-black hover:text-white transition-colors cursor-pointer"
              @click="clearSearch"
            >
              Clear search
            </button>
            <button
              v-else
              type="button"
              class="px-5 py-2.5 rounded-[38px] border border-black text-black font-['Raleway',sans-serif] font-bold text-sm hover:bg-black hover:text-white transition-colors cursor-pointer"
              @click="resetFilters"
            >
              Show all cohorts
            </button>
          </div>
        </div>

        <div
          v-else
          class="w-full h-full flex flex-col justify-between"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[18px] sm:gap-[20px] w-full items-start">
            <a
              v-for="cohort in paginatedCohorts"
              :key="cohort.id"
              :href="`/cohorts/${cohort.id}`"
              data-prefetch="false"
              class="relative group h-[180px] rounded-[14px] border bg-[#f8fff8] p-[16px] flex flex-col hover:z-50 shadow-none hover:shadow-none hover:border-[#01d680] transition-colors duration-200 no-underline"
              :class="selectedCohortIds.includes(cohort.id) ? 'border-[#01d680] ring-2 ring-[#01d680]' : 'border-[#22ec9b]'"
              @click="handleCardClick(cohort, $event)"
            >
              <!-- Selection Checkbox -->
              <div
                class="absolute top-[12px] left-[12px] z-30 transition-opacity"
                :class="selectedCohortIds.length > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
                @click.stop.prevent="toggleSelect(cohort.id)"
              >
                <input
                  type="checkbox"
                  :checked="selectedCohortIds.includes(cohort.id)"
                  class="w-[16px] h-[16px] accent-[#01d680] cursor-pointer rounded"
                  @click.stop
                  @change="toggleSelect(cohort.id)"
                >
              </div>

              <div
                class="flex justify-between items-start w-full transition-all"
                :class="{ 'pl-[22px]': selectedCohortIds.length > 0 }"
              >
                <h3 class="text-[14px] font-bold text-black font-['Raleway',sans-serif] line-clamp-2 min-w-0 flex-1 m-0">{{ cohort.name }}</h3>
                <div
                  class="w-[20px] h-[20px] flex flex-col justify-center items-center cursor-pointer shrink-0 relative group/kebab gap-[2px] ml-[10px]"
                  @click.stop.prevent="toggleKebab(cohort.id)"
                >
                  <div class="w-[3px] h-[3px] bg-black rounded-full" />
                  <div class="w-[3px] h-[3px] bg-black rounded-full" />
                  <div class="w-[3px] h-[3px] bg-black rounded-full" />

                  <!-- Stateful Kebab Dropdown -->
                  <div
                    v-if="openKebabCohortId === cohort.id"
                    class="absolute top-[24px] right-0 bg-white border border-black/10 rounded-[14px] shadow-2xl z-[100] flex flex-col w-[170px] py-2"
                    @click.stop
                  >
                    <a
                      v-if="cohort.can_share"
                      href="#"
                      class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
                      @click.stop.prevent="openShareModal(cohort); closeKebab()"
                    >Share</a>
                    <a
                      href="#"
                      class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
                      @click.stop.prevent="openCloneModal(cohort); closeKebab()"
                    >Clone</a>
                    <a
                      v-if="!cohort.archived_at"
                      href="#"
                      class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
                      @click.stop.prevent="openSaveAsTemplateModal(cohort); closeKebab()"
                    >Save as Template</a>

                    <a
                      v-if="!cohort.archived_at"
                      href="#"
                      class="px-4 py-2 text-sm no-underline text-left block"
                      :class="[canArchive(cohort) ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed opacity-50']"
                      :title="!canArchive(cohort) ? 'Cannot archive while students are actively signing' : ''"
                      @click.stop.prevent="canArchive(cohort) ? (openArchiveModal(cohort), closeKebab()) : null"
                    >Archive</a>
                    <a
                      v-if="cohort.archived_at"
                      href="#"
                      class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
                      @click.stop.prevent="openUnarchiveModal(cohort); closeKebab()"
                    >Unarchive</a>
                    <a
                      v-if="!cohort.archived_at"
                      href="#"
                      class="px-4 py-2 text-sm text-red-600 hover:bg-red-50 no-underline text-left block"
                      @click.stop.prevent="openDeleteModal(cohort); closeKebab()"
                    >Delete</a>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-[6px] mt-[12px] w-full flex-grow">
                <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#5b5c60]">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="shrink-0"
                  ><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
                  <span>{{ formatProgrammesCount(cohort) }}</span>
                </div>
                <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#5b5c60]">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="shrink-0"
                  >
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
                    <path d="M6 12H4a2 2 0 0 0-2 2v8h4" />
                    <path d="M18 9h2a2 2 0 0 1 2 2v11h-4" />
                    <path d="M10 6h4" />
                    <path d="M10 10h4" />
                    <path d="M10 14h4" />
                    <path d="M10 18h4" />
                  </svg>
                  <span>{{ formatSponsorsCount(cohort) }}</span>
                </div>
                <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#292d32]">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="shrink-0"
                  ><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle
                    cx="9"
                    cy="7"
                    r="4"
                  /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  <span>{{ formatStudentsCount(cohort) }}</span>
                </div>
              </div>

              <div class="w-full mt-auto flex items-center gap-[12px]">
                <div
                  class="flex items-center gap-[4px] text-[12px] text-[#5b5c60] font-['Raleway',sans-serif]"
                  title="Draft"
                >
                  <div class="w-[8px] h-[8px] rounded-full bg-[#9b9b9b]" />
                  <span class="font-bold">{{ cohort.programme_status_counts?.draft || 0 }}</span>
                </div>
                <div
                  class="flex items-center gap-[4px] text-[12px] text-[#5b5c60] font-['Raleway',sans-serif]"
                  title="In Progress"
                >
                  <div class="w-[8px] h-[8px] rounded-full bg-[#f97316]" />
                  <span class="font-bold">{{ cohort.programme_status_counts?.in_progress || 0 }}</span>
                </div>
                <div
                  class="flex items-center gap-[4px] text-[12px] text-[#5b5c60] font-['Raleway',sans-serif]"
                  title="Completed"
                >
                  <div class="w-[8px] h-[8px] rounded-full bg-[#01d680]" />
                  <span class="font-bold">{{ cohort.programme_status_counts?.completed || 0 }}</span>
                </div>
                <div
                  class="flex items-center gap-[4px] text-[12px] text-[#5b5c60] font-['Raleway',sans-serif]"
                  title="Declined"
                >
                  <div class="w-[8px] h-[8px] rounded-full bg-[#ef4444]" />
                  <span class="font-bold">{{ cohort.programme_status_counts?.declined || 0 }}</span>
                </div>
              </div>

            </a>
          </div>

          <!-- Cohorts Pagination -->
          <div
            v-if="currentPagy"
            class="flex items-center justify-between mt-[32px] pt-[16px] border-t border-gray-200/60 w-full shrink-0"
          >
            <div class="text-[14px] text-[#5b5c60] font-['Raleway',sans-serif]">
              Showing {{ currentPagy.from }}–{{ currentPagy.to }} of {{ currentPagy.count }} cohorts
            </div>
            <div class="flex items-center gap-[12px]">
              <a
                v-if="currentPagy.prev"
                :href="pageUrl(currentPagy.prev)"
                data-prefetch="false"
                class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
                @click.prevent="goToPage(currentPagy.prev)"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M13.5 8.5L9.5 12L13.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </a>
              <div
                v-else
                class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M13.5 8.5L9.5 12L13.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </div>
              <span class="text-[14px] font-semibold text-[#000000] font-['Raleway',sans-serif] px-1">
                Page {{ currentPagy.page }} of {{ currentPagy.last }}
              </span>
              <a
                v-if="currentPagy.next"
                :href="pageUrl(currentPagy.next)"
                data-prefetch="false"
                class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
                @click.prevent="goToPage(currentPagy.next)"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M10.5 8.5L14.5 12L10.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </a>
              <div
                v-else
                class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
              >
                <svg
                  class="w-[18px] h-[18px]"
                  viewBox="0 0 24 24"
                  fill="none"
                ><circle
                  cx="12"
                  cy="12"
                  r="9.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                /><path
                  d="M10.5 8.5L14.5 12L10.5 15.5"
                  stroke="currentColor"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CloneCohortModal
      v-if="showCloneModal && cohortToClone"
      :cohort="cohortToClone"
      @close="closeCloneModal"
    />
    <ArchiveCohortModal
      v-if="showArchiveModal && cohortToArchive"
      :cohort="cohortToArchive"
      @close="closeArchiveModal"
    />
    <UnarchiveCohortModal
      v-if="showUnarchiveModal && cohortToUnarchive"
      :cohort="cohortToUnarchive"
      @close="closeUnarchiveModal"
    />
    <DeleteCohortModal
      v-if="showDeleteModal && cohortToDelete"
      :cohort="cohortToDelete"
      @close="closeDeleteModal"
      @deleted="onCohortDeleted"
    />
    <BulkDeleteCohortModal
      v-if="showBulkDeleteModal && selectedCohortIds.length > 0"
      :cohort-ids="selectedCohortIds"
      :cohorts="selectedCohorts"
      @close="closeBulkDeleteModal"
      @deleted="onBulkCohortsDeleted"
    />
    <BulkDeleteCohortModal
      v-if="showBulkDeleteTemplateModal && selectedTemplateIds.length > 0"
      :cohort-ids="selectedTemplateIds"
      :cohorts="selectedTemplates"
      item-type="template"
      endpoint="/cohort_templates/bulk_destroy.json"
      @close="closeBulkDeleteTemplateModal"
      @deleted="onBulkTemplatesDeleted"
    />
    <InstantiateCohortModal
      v-if="showInstantiateModal && selectedTemplateForInstantiate"
      :template="selectedTemplateForInstantiate"
      @close="closeInstantiateModal"
      @instantiated="onTemplateInstantiated"
    />
    <SaveAsCohortTemplateModal
      v-if="showSaveAsTemplateModal && cohortToSaveAsTemplate"
      :cohort="cohortToSaveAsTemplate"
      @close="closeSaveAsTemplateModal"
      @saved="onCohortSavedAsTemplate"
    />

    <!-- Mobile Floating Action Button (FAB, consistent with Dashboard & Folios) -->
    <div
      id="cohorts-mobile-fab-container"
      class="fixed bottom-[74px] right-4 sm:right-6 z-40 lg:hidden"
    >
      <mount-on-click
        v-if="canCreate"
        data-template-id="add-cohort-wizard-template"
      >
        <button
          type="button"
          aria-label="Create new cohort"
          class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#01d680] hover:bg-[#00c270] text-[#043f42] shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#01d680]/30 active:scale-95"
        >
          <svg
            class="w-6 h-6 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
        </button>
      </mount-on-click>
      <subscription-gate v-else>
        <button
          type="button"
          aria-label="Create new cohort"
          class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#01d680] hover:bg-[#00c270] text-[#043f42] shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#01d680]/30 active:scale-95"
        >
          <svg
            class="w-6 h-6 transition-transform duration-200"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
        </button>
      </subscription-gate>
    </div>
  </div>
</template>

<script>
import CloneCohortModal from './CloneCohortModal.vue'
import ArchiveCohortModal from './ArchiveCohortModal.vue'
import UnarchiveCohortModal from './UnarchiveCohortModal.vue'
import DeleteCohortModal from './DeleteCohortModal.vue'
import BulkDeleteCohortModal from './BulkDeleteCohortModal.vue'
import CohortTemplateCard from './CohortTemplateCard.vue'
import InstantiateCohortModal from './InstantiateCohortModal.vue'
import SaveAsCohortTemplateModal from './SaveAsCohortTemplateModal.vue'

export default {
  name: 'CohortsList',
  components: {
    CloneCohortModal,
    ArchiveCohortModal,
    UnarchiveCohortModal,
    DeleteCohortModal,
    BulkDeleteCohortModal,
    CohortTemplateCard,
    InstantiateCohortModal,
    SaveAsCohortTemplateModal
  },
  props: {
    cohorts: { type: Array, required: true },
    currentFilter: { type: String, default: 'all' },
    query: { type: String, default: '' },
    canCreate: { type: Boolean, default: false },
    canCreateCohortTemplate: { type: Boolean, default: false },
    totalCount: { type: Number, default: 0 },
    pagy: { type: Object, default: () => null },
    currentUserId: { type: Number, default: null },
    activeTab: { type: String, default: 'cohorts' },
    templates: { type: Array, default: () => [] }
  },
  data () {
    return {
      activeTabName: this.activeTab || 'cohorts',
      cohortList: [...(this.cohorts || [])],
      templateList: [...(this.templates || [])],
      currentPagy: this.pagy,
      serverQuery: this.query || '',
      isLoading: false,
      activeFilter: this.currentFilter || 'all',
      searchQuery: this.query || '',
      currentPage: this.pagy?.page || 1,
      perPage: 10,
      selectedCohortIds: [],
      selectedTemplateIds: [],
      showCloneModal: false,
      cohortToClone: null,
      showArchiveModal: false,
      cohortToArchive: null,
      showUnarchiveModal: false,
      cohortToUnarchive: null,
      showDeleteModal: false,
      cohortToDelete: null,
      showBulkDeleteModal: false,
      showBulkDeleteTemplateModal: false,
      showInstantiateModal: false,
      selectedTemplateForInstantiate: null,
      showSaveAsTemplateModal: false,
      cohortToSaveAsTemplate: null,
      isFilterMenuOpen: false,
      openKebabCohortId: null,
      isFilterSortOpen: false,
      currentSort: 'date-desc'
    }
  },
  computed: {
    hasCohorts () {
      return (this.cohortList && this.cohortList.length > 0) || this.totalCount > 0
    },
    statusPills () {
      if (this.activeTabName === 'templates') {
        return [
          { key: 'all', label: 'All templates' },
          { key: 'active', label: 'Active' },
          { key: 'archived', label: 'Archived' }
        ]
      }
      return [
        { key: 'all', label: 'All' },
        { key: 'active', label: 'Active' },
        { key: 'completed', label: 'Completed' },
        { key: 'archived', label: 'Archived' },
        { key: 'created_by_me', label: 'Created by me' },
        { key: 'shared_with_me', label: 'Shared with me' }
      ]
    },
    filteredTemplates () {
      let list = [...(this.templateList || [])]
      const q = this.searchQuery.trim().toLowerCase()
      if (q) {
        list = list.filter(t =>
          (t.name || '').toLowerCase().includes(q) ||
          (t.seta_type || '').toLowerCase().includes(q) ||
          (t.qualification_id || '').toLowerCase().includes(q)
        )
      }
      if (this.currentSort === 'name-asc') {
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } else if (this.currentSort === 'name-desc') {
        list.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      } else if (this.currentSort === 'date-asc') {
        list.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
      } else if (this.currentSort === 'date-desc') {
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      }
      return list
    },
    selectedCohorts () {
      return (this.cohortList || []).filter(c => this.selectedCohortIds.includes(c.id))
    },
    selectedTemplates () {
      return (this.templateList || []).filter(t => this.selectedTemplateIds.includes(t.id))
    },
    isClientFiltered () {
      return this.searchQuery.trim() !== (this.serverQuery || '').trim()
    },
    filteredCohorts () {
      let list = [...(this.cohortList || [])]

      // Search query filtering (live client-filter while typing)
      const q = this.searchQuery.trim().toLowerCase()
      const sq = (this.serverQuery || '').trim().toLowerCase()
      if (q && q !== sq) {
        list = list.filter(cohort => {
          const nameMatch = (cohort.name || '').toLowerCase().includes(q)
          const legacySponsorMatch = (cohort.sponsor_email || '').toLowerCase().includes(q)
          const progSponsorMatch = (cohort.programmes || []).some(p =>
            (p.sponsors || []).some(s =>
              (s.email || '').toLowerCase().includes(q) ||
              (s.company_name || '').toLowerCase().includes(q) ||
              (s.name || '').toLowerCase().includes(q)
            ) ||
            (p.name || '').toLowerCase().includes(q) ||
            (p.qualification_id || '').toLowerCase().includes(q)
          )
          return nameMatch || legacySponsorMatch || progSponsorMatch
        })
      }

      // Status filtering fallback if pagy is absent
      if (!this.currentPagy) {
        if (this.activeFilter === 'archived') {
          list = list.filter(c => !!c.archived_at || !!c.deleted_at)
        } else if (this.activeFilter === 'active') {
          list = list.filter(c => !c.archived_at && !c.deleted_at && (c.status === 'active' || (c.programmes || []).some(p => ['active', 'in_progress', 'draft'].includes(p.status) || ['active', 'in_progress'].includes(p.derived_status))))
        } else if (this.activeFilter === 'completed') {
          list = list.filter(c => !c.archived_at && !c.deleted_at && (c.status === 'completed' || (c.programmes || []).some(p => p.status === 'completed' || p.derived_status === 'completed')))
        } else if (this.activeFilter === 'created_by_me' && this.currentUserId) {
          list = list.filter(c => !c.archived_at && !c.deleted_at && c.author_id === this.currentUserId)
        } else if (this.activeFilter === 'shared_with_me' && this.currentUserId) {
          list = list.filter(c => !c.archived_at && !c.deleted_at && c.author_id !== this.currentUserId)
        }
      }

      // Sorting
      if (this.currentSort === 'name-asc') {
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } else if (this.currentSort === 'name-desc') {
        list.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      } else if (this.currentSort === 'date-asc') {
        list.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
      } else {
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      }

      return list
    },
    totalPages () {
      return Math.ceil(this.filteredCohorts.length / this.perPage) || 1
    },
    paginatedCohorts () {
      if (this.currentPagy && !this.isClientFiltered) {
        return this.filteredCohorts
      }
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredCohorts.slice(start, start + this.perPage)
    },
    paginationFrom () {
      if (this.filteredCohorts.length === 0) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationTo () {
      return Math.min(this.currentPage * this.perPage, this.filteredCohorts.length)
    },
    emptyStateTitle () {
      if (this.searchQuery.trim()) {
        return 'No matching cohorts found'
      }
      switch (this.activeFilter) {
        case 'active': return 'No active cohorts yet'
        case 'completed': return 'No completed cohorts yet'
        case 'archived': return 'No archived cohorts yet'
        case 'created_by_me': return 'No cohorts created by you'
        case 'shared_with_me': return 'No cohorts shared with you'
        default: return 'No matching cohorts found'
      }
    },
    emptyStateSubtitle () {
      return this.searchQuery.trim() ? `No cohorts matched "${this.searchQuery}".` : 'There are currently no cohorts in this view.'
    }
  },
  watch: {
    cohorts: {
      handler (val) {
        this.cohortList = [...(val || [])]
      },
      deep: true
    },
    templates: {
      handler (val) {
        this.templateList = [...(val || [])]
      },
      deep: true
    },
    pagy (val) {
      this.currentPagy = val
      if (val && val.page) {
        this.currentPage = val.page
      }
    },
    activeTab (val) {
      if (val) {
        this.activeTabName = val
      }
    },
    currentFilter (val) {
      if (val) {
        this.activeFilter = val
      }
    },
    query (val) {
      this.searchQuery = val || ''
      this.serverQuery = val || ''
    }
  },
  mounted () {
    this.handleOutsideClick = () => {
      this.isFilterMenuOpen = false
      this.openKebabCohortId = null
      this.isFilterSortOpen = false
    }
    document.addEventListener('click', this.handleOutsideClick)
    this.handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search)
      this.activeTabName = urlParams.get('tab') === 'templates' ? 'templates' : 'cohorts'
      this.activeFilter = urlParams.get('status') || 'all'
      this.searchQuery = urlParams.get('q') || ''
      this.serverQuery = this.searchQuery
      this.currentPage = parseInt(urlParams.get('page'), 10) || 1
      const sortParam = urlParams.get('sort')
      const revSortMap = {
        name_asc: 'name-asc',
        name_desc: 'name-desc',
        oldest: 'date-asc',
        newest: 'date-desc'
      }
      if (sortParam && revSortMap[sortParam]) {
        this.currentSort = revSortMap[sortParam]
      }
      this.fetchData()
    }
    window.addEventListener('popstate', this.handlePopState)
  },
  beforeUnmount () {
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick)
    }
    if (this.handlePopState) {
      window.removeEventListener('popstate', this.handlePopState)
    }
  },
  methods: {
    toggleFilterSort () {
      this.isFilterSortOpen = !this.isFilterSortOpen
    },
    setSort (sort) {
      this.currentSort = sort
      this.isFilterSortOpen = false
    },
    selectFilterFromMenu (filter) {
      this.setFilter(filter)
      this.isFilterSortOpen = false
    },
    toggleSelect (id) {
      const index = this.selectedCohortIds.indexOf(id)
      if (index > -1) {
        this.selectedCohortIds.splice(index, 1)
      } else {
        if (this.selectedCohortIds.length >= 100) {
          alert('You can select a maximum of 100 cohorts at a time.')
          return
        }
        this.selectedCohortIds.push(id)
      }
    },
    selectAllVisible () {
      const visibleIds = this.paginatedCohorts.map(c => c.id)
      this.selectedCohortIds = Array.from(new Set([...this.selectedCohortIds, ...visibleIds]))
    },
    clearSelection () {
      this.selectedCohortIds = []
    },
    toggleSelectTemplate (id) {
      const index = this.selectedTemplateIds.indexOf(id)
      if (index > -1) {
        this.selectedTemplateIds.splice(index, 1)
      } else {
        if (this.selectedTemplateIds.length >= 100) {
          alert('You can select a maximum of 100 templates at a time.')
          return
        }
        this.selectedTemplateIds.push(id)
      }
    },
    selectAllVisibleTemplates () {
      const visibleIds = this.filteredTemplates.map(t => t.id)
      this.selectedTemplateIds = Array.from(new Set([...this.selectedTemplateIds, ...visibleIds]))
    },
    clearTemplateSelection () {
      this.selectedTemplateIds = []
    },
    openBulkDeleteTemplateModal () {
      this.showBulkDeleteTemplateModal = true
    },
    closeBulkDeleteTemplateModal () {
      this.showBulkDeleteTemplateModal = false
    },
    onBulkTemplatesDeleted (deletedIds) {
      if (Array.isArray(deletedIds) && deletedIds.length > 0) {
        this.templateList = this.templateList.filter(i => !deletedIds.includes(i.id))
      }
      this.clearTemplateSelection()
      this.closeBulkDeleteTemplateModal()
      this.fetchData()
    },
    handleCardClick (cohort, event) {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        this.toggleSelect(cohort.id)
      }
    },
    openDeleteModal (cohort) {
      this.cohortToDelete = cohort
      this.showDeleteModal = true
    },
    closeDeleteModal () {
      this.cohortToDelete = null
      this.showDeleteModal = false
    },
    openBulkDeleteModal () {
      this.showBulkDeleteModal = true
    },
    closeBulkDeleteModal () {
      this.showBulkDeleteModal = false
    },
    onCohortDeleted (id) {
      this.cohortList = this.cohortList.filter(i => i.id !== id)
      this.selectedCohortIds = this.selectedCohortIds.filter(i => i !== id)
      this.closeDeleteModal()
    },
    onBulkCohortsDeleted (deletedIds) {
      if (Array.isArray(deletedIds) && deletedIds.length > 0) {
        this.cohortList = this.cohortList.filter(i => !deletedIds.includes(i.id))
      }
      this.clearSelection()
      this.closeBulkDeleteModal()
      this.fetchData()
    },
    openShareModal (cohort) {
      if (window.openShareModal) {
        window.openShareModal({
          assetType: 'Cohort',
          assetId: cohort.id,
          assetName: cohort.name
        })
      }
    },
    openCloneModal (cohort) {
      if (!this.canCreate) {
        document.getElementById('subscription_gate_modal')?.showModal()
        return
      }
      this.cohortToClone = cohort
      this.showCloneModal = true
    },
    closeCloneModal () {
      this.showCloneModal = false
      this.cohortToClone = null
    },
    openArchiveModal (cohort) {
      this.cohortToArchive = cohort
      this.showArchiveModal = true
    },
    closeArchiveModal () {
      this.showArchiveModal = false
      this.cohortToArchive = null
    },
    openUnarchiveModal (cohort) {
      this.cohortToUnarchive = cohort
      this.showUnarchiveModal = true
    },
    closeUnarchiveModal () {
      this.showUnarchiveModal = false
      this.cohortToUnarchive = null
    },
    formatProgrammesCount (cohort) {
      const count = cohort.programmes?.length || 0
      return count === 1 ? '1 Program' : `${count} Programs`
    },
    formatSponsorsCount (cohort) {
      let count = 0
      if (cohort.programmes && cohort.programmes.length > 0) {
        const uniqueSponsors = new Set()
        cohort.programmes.forEach(p => {
          if (p.sponsors && p.sponsors.length > 0) {
            p.sponsors.forEach(s => {
              const key = s.email || s.company_name || s.name
              if (key) uniqueSponsors.add(key)
              else count += 1
            })
          }
        })
        count += uniqueSponsors.size
      } else if (cohort.sponsors && cohort.sponsors.length > 0) {
        count = cohort.sponsors.length
      } else if (cohort.sponsor_email) {
        count = 1
      }
      return count === 1 ? '1 Sponsor' : `${count} Sponsors`
    },
    formatStudentsCount (cohort) {
      const count = cohort.enrollments_count || 0
      return count === 1 ? '1 Student' : `${count} Students`
    },
    canArchive (cohort) {
      if (['draft', 'completed'].includes(cohort.status)) return true
      if (cohort.in_motion_enrollments_count > 0) return false
      return true
    },
    toggleKebab (cohortId) {
      this.openKebabCohortId = this.openKebabCohortId === cohortId ? null : cohortId
    },
    closeKebab () {
      this.openKebabCohortId = null
    },
    pillClass (filter) {
      const isActive = this.activeFilter === filter
      const base = "flex flex-row gap-[10px] px-[16px] py-[8px] justify-center items-center rounded-full transition-colors cursor-pointer no-underline border text-[13px] sm:text-[14px] font-['Raleway',sans-serif] whitespace-nowrap shrink-0"
      return isActive
        ? `${base} border-black bg-transparent text-black font-semibold`
        : `${base} border-transparent bg-[#9b9b9b]/25 text-black hover:bg-[#9b9b9b]/40 font-normal`
    },
    filterUrl (filter) {
      const params = new URLSearchParams()
      if (this.activeTabName === 'templates') {
        params.set('tab', 'templates')
      }
      if (filter && filter !== 'all') params.set('status', filter)
      if (this.searchQuery && this.searchQuery.trim()) {
        params.set('q', this.searchQuery.trim())
      }
      const qs = params.toString()
      return qs ? `/cohorts?${qs}` : '/cohorts'
    },
    pageUrl (page) {
      const params = new URLSearchParams()
      if (this.activeTabName === 'templates') {
        params.set('tab', 'templates')
      } else if (this.activeFilter && this.activeFilter !== 'all') {
        params.set('status', this.activeFilter)
      }
      if (this.searchQuery && this.searchQuery.trim()) params.set('q', this.searchQuery.trim())
      if (page && page > 1) params.set('page', page)
      const qs = params.toString()
      return qs ? `/cohorts?${qs}` : '/cohorts'
    },
    updateUrl () {
      const params = new URLSearchParams()
      if (this.activeTabName === 'templates') {
        params.set('tab', 'templates')
      } else if (this.activeFilter && this.activeFilter !== 'all') {
        params.set('status', this.activeFilter)
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        params.set('q', this.searchQuery.trim())
      }
      if (this.currentPage && this.currentPage > 1) {
        params.set('page', this.currentPage)
      }
      const sortMap = {
        'name-asc': 'name_asc',
        'name-desc': 'name_desc',
        'date-asc': 'oldest',
        'date-desc': 'newest'
      }
      if (sortMap[this.currentSort] && sortMap[this.currentSort] !== 'newest') {
        params.set('sort', sortMap[this.currentSort])
      }
      const qs = params.toString()
      const newUrl = qs ? `/cohorts?${qs}` : '/cohorts'
      window.history.pushState({
        tab: this.activeTabName,
        filter: this.activeFilter,
        page: this.currentPage,
        query: this.searchQuery,
        sort: this.currentSort
      }, '', newUrl)
    },
    async fetchData () {
      this.isLoading = true
      try {
        const params = new URLSearchParams()
        if (this.activeTabName === 'templates') {
          params.set('tab', 'templates')
        } else if (this.activeFilter && this.activeFilter !== 'all') {
          params.set('status', this.activeFilter)
        }
        if (this.searchQuery && this.searchQuery.trim()) {
          params.set('q', this.searchQuery.trim())
        }
        if (this.currentPage && this.currentPage > 1) {
          params.set('page', this.currentPage)
        }
        const sortMap = {
          'name-asc': 'name_asc',
          'name-desc': 'name_desc',
          'date-asc': 'oldest',
          'date-desc': 'newest'
        }
        if (sortMap[this.currentSort]) {
          params.set('sort', sortMap[this.currentSort])
        }
        const qs = params.toString()
        const url = `/cohorts.json${qs ? `?${qs}` : ''}`
        const res = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        if (res.ok) {
          const data = await res.json()
          if (this.activeTabName === 'templates') {
            this.templateList = data.templates || []
            this.currentPagy = data.pagy
          } else {
            this.cohortList = data.cohorts || []
            this.currentPagy = data.pagy
          }
          this.selectedCohortIds = []
        }
      } catch (err) {
        console.error('Failed to fetch cohorts data', err)
      } finally {
        this.isLoading = false
      }
    },
    async switchTab (tab) {
      if (this.activeTabName === tab) return
      this.activeTabName = tab
      this.activeFilter = 'all'
      this.searchQuery = ''
      this.serverQuery = ''
      this.currentPage = 1
      this.selectedCohortIds = []
      this.selectedTemplateIds = []
      this.updateUrl()
      await this.fetchData()
    },
    openSaveAsTemplateModal (cohort) {
      if (!this.canCreateCohortTemplate) {
        document.getElementById('subscription_gate_modal')?.showModal()
        return
      }
      this.cohortToSaveAsTemplate = cohort
      this.showSaveAsTemplateModal = true
    },
    closeSaveAsTemplateModal () {
      this.cohortToSaveAsTemplate = null
      this.showSaveAsTemplateModal = false
    },
    onCohortSavedAsTemplate (template) {
      this.closeSaveAsTemplateModal()
      if (template) {
        this.templateList.unshift(template)
        this.switchTab('templates')
      }
    },
    openInstantiateModal (template) {
      if (!this.canCreate) {
        document.getElementById('subscription_gate_modal')?.showModal()
        return
      }
      this.selectedTemplateForInstantiate = template
      this.showInstantiateModal = true
    },
    closeInstantiateModal () {
      this.selectedTemplateForInstantiate = null
      this.showInstantiateModal = false
    },
    onTemplateInstantiated (newCohort) {
      this.closeInstantiateModal()
      if (newCohort && newCohort.id) {
        window.location.href = `/cohorts/${newCohort.id}`
      } else {
        window.location.href = '/cohorts'
      }
    },
    async cloneTemplate (template) {
      if (!this.canCreateCohortTemplate) {
        document.getElementById('subscription_gate_modal')?.showModal()
        return
      }
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const res = await fetch(`/cohort_templates/${template.id}/clone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          }
        })
        if (!res.ok) {
          const data = await res.json()
          if (res.status === 403) {
            const gateModal = document.getElementById('subscription_gate_modal')
            if (gateModal) {
              gateModal.showModal()
              return
            }
          }
          alert(data.error || 'Failed to clone template')
          return
        }
        const data = await res.json()
        const cloned = data.template || data
        this.templateList.unshift(cloned)
      } catch (err) {
        alert(err.message || 'Failed to clone template')
      }
    },
    async archiveTemplate (template) {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const res = await fetch(`/cohort_templates/${template.id}/archive`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          }
        })
        if (!res.ok) {
          const data = await res.json()
          alert(data.error || 'Failed to archive template')
          return
        }
        this.templateList = this.templateList.filter(t => t.id !== template.id)
      } catch (err) {
        alert(err.message || 'Failed to archive template')
      }
    },
    async deleteTemplate (template) {
      if (!confirm(`Are you sure you want to permanently delete "${template.name}"? This action cannot be undone.`)) {
        return
      }
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const res = await fetch(`/cohort_templates/${template.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          }
        })
        if (!res.ok) {
          const data = await res.json()
          alert(data.error || 'Failed to delete template')
          return
        }
        this.templateList = this.templateList.filter(t => t.id !== template.id)
      } catch (err) {
        alert(err.message || 'Failed to delete template')
      }
    },
    async setFilter (filter) {
      if (this.activeFilter === filter) return
      this.activeFilter = filter
      this.currentPage = 1
      this.updateUrl()
      await this.fetchData()
    },
    async goToPage (page) {
      if (!page || page === this.currentPage) return
      this.currentPage = page
      this.updateUrl()
      await this.fetchData()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async submitSearch () {
      this.currentPage = 1
      this.serverQuery = this.searchQuery.trim()
      this.updateUrl()
      await this.fetchData()
    },
    async clearSearch () {
      this.searchQuery = ''
      this.serverQuery = ''
      this.currentPage = 1
      this.updateUrl()
      await this.fetchData()
    },
    async resetFilters () {
      this.activeFilter = 'all'
      this.searchQuery = ''
      this.serverQuery = ''
      this.currentPage = 1
      this.updateUrl()
      await this.fetchData()
    },
    onSearchInput () {
      this.currentPage = 1
    },
    cohortStatus (cohort) {
      if (cohort.archived_at) return 'archived'
      const progs = cohort.programmes || []
      if (progs.some(p => p.status === 'completed')) return 'completed'
      if (progs.some(p => p.status === 'active')) return 'active'
      return cohort.status || 'draft'
    }
  }
}
</script>
